'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';

export default function SetupGuidePage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Setting Up Your Voley Account</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          
          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-brand transition-colors">Overview</a>
                <a href="#initial-setup" className="block text-sm text-gray-600 hover:text-brand transition-colors">Initial Setup</a>
                <a href="#team-configuration" className="block text-sm text-gray-600 hover:text-brand transition-colors">Integration Setup</a>
                <a href="#first-message" className="block text-sm text-gray-600 hover:text-brand transition-colors">Setup Complete</a>
                <a href="#next-steps" className="block text-sm text-gray-600 hover:text-brand transition-colors">Common Issues & Support</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Setting Up Your Voley Account</h1>
              <p className="text-xl text-gray-600">
                A complete walkthrough of getting your agency set up and ready to communicate with clients through Voley.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 15-20 minutes</span>
                <span className="mx-2">•</span>
                <span>Last updated: July 2025</span>
              </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">
              
              <section id="overview" className="mb-12">
                <h2>Overview</h2>
                <p className="text-gray-600 mb-4">
                  When an insurance agency signs up for Voley, they go through a structured onboarding process that takes about 15-20 minutes to complete the initial setup, with phone number approval taking an additional 7-10 business days.
                </p>
                <p className="text-gray-600 mb-6">
                  The process includes account creation, company information, phone number setup, optional integrations, contact import, and payment configuration.
                </p>
                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5">
                  <div className="flex">
                    <div className="shrink-0">
                      <svg className="h-5 w-5 text-brand" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">
                        <strong>Need Help?</strong> If you have any questions or concerns during the setup process, please don&apos;t hesitate to reach out to our support team for assistance.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="initial-setup" className="mb-12">
                <h2>Initial Setup</h2>
                
                <h3 className="text-xl font-semibold mt-8 mb-4">Step 1: Account Creation</h3>
                <p className="text-gray-600 mb-4">
                  Agencies start by visiting the signup page and entering:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6">
                  <li><strong>Work Email:</strong> Their business email address (becomes their login)</li>
                  <li><strong>Password:</strong> Account password</li>
                  <li><strong>First & Last Name:</strong> The account owner&apos;s name</li>
                  <li><strong>Company Name:</strong> The agency&apos;s business name</li>
                </ul>
                <p className="text-gray-600 mb-6">
                  After clicking &quot;Create Account,&quot; they&apos;re automatically logged in and taken to the next step.
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4">Step 2: Company Information</h3>
                <p className="text-gray-600 mb-4">
                  The system collects information about the agency to customize their experience:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6">
                  <li><strong>Company Size:</strong> Number of employees (1-5, 6-20, 21-50, 50+)</li>
                  <li><strong>Client Count:</strong> Approximate number of clients they serve</li>
                  <li><strong>Industry Type:</strong> Primary insurance focus (Auto, Home, Commercial, Life, Health, etc.)</li>
                  <li><strong>Current Software:</strong> Existing systems they use (HawkSoft, Applied Epic, Agency Zoom, etc.)</li>
                  <li><strong>Business Goals:</strong> What they want to achieve with Voley</li>
                </ul>
                <p className="text-gray-600 mb-6">
                  This information helps determine pricing tiers and available integrations.
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4">Step 3: Phone Number Setup</h3>
                <p className="text-gray-600 mb-4">
                  Agencies choose between two options:
                </p>
                
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6">
                  <h4 className="font-semibold mb-3 text-gray-900">Option A: Get a New Number</h4>
                  <p className="text-gray-600 mb-3">Most agencies choose to get a new dedicated number for texting. They provide:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
                    <li><strong>Business Information:</strong> Legal business name, business type (LLC, Corporation, etc.), EIN, and business website</li>
                    <li><strong>Authorized Representative:</strong> Name, title, phone, and email of someone who can represent the business</li>
                    <li><strong>Business Address:</strong> Physical business address (P.O. boxes not allowed)</li>
                  </ul>
                  <p className="text-gray-600">
                    The system then creates a Trust Hub profile, registers the brand with The Campaign Registry (3-5 business days), registers the messaging campaign (1-3 business days), and assigns a phone number once approved.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6">
                  <h4 className="font-semibold mb-3 text-gray-900">Option B: Port Existing Number</h4>
                  <p className="text-gray-600 mb-3">Some agencies prefer to use their current business number. This requires:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Proof of ownership of the existing number</li>
                    <li>Letter of Authorization (LOA) for number porting</li>
                    <li>Verification that the number supports SMS/MMS messaging</li>
                  </ul>
                </div>
              </section>

              <section id="team-configuration" className="mb-12">
                <h2>Integration Setup (Optional)</h2>
                <p className="text-gray-600 mb-6">
                  Agencies can connect their existing management systems to automatically sync contacts and log conversations.
                </p>

                <div className="bg-brand-green/10 border border-brand-green/30 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">HawkSoft Integration</h3>
                  <p className="text-gray-600 mb-3">Agencies with HawkSoft provide:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
                    <li>Client ID and Client Secret from their HawkSoft API settings</li>
                    <li>Agency ID from their HawkSoft account</li>
                    <li>Environment selection (Sandbox for testing, Production for live use)</li>
                  </ul>
                  <p className="text-gray-600 mb-3">Once connected, the integration:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Automatically imports client contacts</li>
                    <li>Logs all messages and conversations back to client records</li>
                    <li>Keeps contact information synchronized</li>
                    <li>Maps conversations to the correct clients</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Other Integrations</h3>
                  <p className="text-gray-600">
                    Additional AMS/CRM integrations are currently under construction. Contact our support team for updates on availability.
                  </p>
                </div>

                <h2 className="text-2xl font-semibold mt-12 mb-6">Contact Import</h2>
                <p className="text-gray-600 mb-6">
                  Agencies get their client contacts into Voley through:
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4">HawkSoft Contact Sync</h3>
                <p className="text-gray-600 mb-4">
                  If you&apos;ve connected HawkSoft, you can import all your contacts with a single sync. Here&apos;s how it works:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
                  <li>Run a manual sync from Settings to import all existing contacts from HawkSoft</li>
                  <li>Contact information maps automatically to the appropriate fields in Voley</li>
                  <li>All conversations are logged back to the correct client records in HawkSoft</li>
                </ul>
                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700">
                    <strong>HawkSoft is your source of truth.</strong> Voley never modifies contact information in HawkSoft — we only read from it. When you make changes in HawkSoft (updating contact info, removing clients, merging records, etc.), simply run another manual sync in Voley and your contact list will update to match.
                  </p>
                </div>

                <h3 className="text-xl font-semibold mt-8 mb-4">Manual Upload</h3>
                <p className="text-gray-600 mb-4">
                  Agencies can upload a CSV file with their contacts. The system expects:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6">
                  <li><strong>Required fields:</strong> First Name, Last Name, Phone Number (in +1XXXXXXXXXX format)</li>
                  <li><strong>Optional fields:</strong> Email Address, Company Name, Address, Policy Numbers, Notes</li>
                </ul>

                <h3 className="text-xl font-semibold mt-8 mb-4">Individual Entry</h3>
                <p className="text-gray-600 mb-6">
                  Smaller agencies can add contacts one at a time through the dashboard.
                </p>

                <h2 className="text-2xl font-semibold mt-12 mb-6">Payment Setup</h2>
                <p className="text-gray-600 mb-4">
                  Agencies provide billing information to activate their account:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6">
                  <li><strong>Payment method:</strong> Credit card or bank account (ACH for annual billing)</li>
                  <li><strong>Billing address:</strong> Where invoices should be sent</li>
                  <li><strong>Tax information:</strong> Business tax ID if applicable</li>
                  <li><strong>Billing contact:</strong> Primary contact for billing issues</li>
                </ul>
                <p className="text-gray-600 mb-6">
                  The system offers both monthly and annual billing options, with annual billing providing a discount.
                </p>
              </section>

              <section id="first-message" className="mb-12">
                <h2>Setup Complete</h2>
                <p className="text-gray-600 mb-6">
                  Once the initial setup is finished, agencies have:
                </p>

                <div className="bg-brand-green/10 border border-brand-green/30 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Immediate Access</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Full dashboard access for managing contacts and conversations</li>
                    <li>Team chat functionality for internal communication</li>
                    <li>Ability to organize contacts into folders and groups</li>
                    <li>Team member management to add additional users</li>
                  </ul>
                </div>

                <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Pending Phone Number</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Phone number registration processes in the background (7-10 business days)</li>
                    <li>Email notifications keep the agency updated on approval status</li>
                    <li>Once approved, SMS/MMS messaging becomes available immediately</li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold mt-8 mb-4">Team Management</h3>
                <p className="text-gray-600 mb-4">
                  After setup, agencies can add team members to their account:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6">
                  <li><strong>Invitation process:</strong> Agency owner invites users via email from Settings &gt; Team Members</li>
                  <li><strong>Role assignment:</strong> Admin (full access), User (messaging and contacts), or Read-Only (view only)</li>
                  <li><strong>Account access:</strong> Team members create their own passwords and join the agency account</li>
                  <li><strong>Billing:</strong> No additional cost for team members regardless of count</li>
                </ul>
              </section>

              <section id="next-steps" className="mb-12">
                <h2>Common Setup Situations</h2>
                
                <div className="bg-red-50/50 border border-red-200/50 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Business Registration Issues</h3>
                  <p className="text-gray-600 mb-3">The most common approval delays occur when:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Business website doesn&apos;t match the registered business name</li>
                    <li>EIN doesn&apos;t match the legal business name</li>
                    <li>Website lacks privacy policy or terms of service</li>
                    <li>Residential address used instead of business address</li>
                  </ul>
                </div>

                <div className="bg-orange-50/50 border border-orange-200/50 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Integration Connection Problems</h3>
                  <p className="text-gray-600 mb-3">HawkSoft integration issues typically involve:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Incorrect Client ID or Client Secret from HawkSoft</li>
                    <li>Wrong Agency ID entered</li>
                    <li>API access not enabled in the HawkSoft system</li>
                    <li>Environment mismatch (Sandbox vs Production)</li>
                  </ul>
                </div>

                <div className="bg-brand/10 border border-brand/20 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Contact Import Challenges</h3>
                  <p className="text-gray-600 mb-3">Contact import failures usually happen when:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Phone numbers aren&apos;t in the required +1XXXXXXXXXX format</li>
                    <li>CSV files missing required headers (First Name, Last Name, Phone)</li>
                    <li>File size exceeds the system limit</li>
                    <li>Special characters in contact names cause formatting issues</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-semibold mt-12 mb-6">Support and Next Steps</h2>
                <p className="text-gray-600 mb-4">
                  Once agencies complete setup, they typically:
                </p>
                <ol className="list-decimal pl-6 text-gray-600 mb-6">
                  <li><strong>Wait for phone number approval</strong> (receive email updates on status)</li>
                  <li><strong>Explore the dashboard</strong> while waiting for messaging activation</li>
                  <li><strong>Set up team members</strong> and organize contacts into folders</li>
                  <li><strong>Configure message templates</strong> and business hours</li>
                  <li><strong>Begin messaging clients</strong> once phone number is approved</li>
                </ol>
                <p className="text-gray-600">
                  The platform provides support through in-app help, email support, and live chat during business hours. Most agencies are successfully messaging clients within their first week of approval.
                </p>
              </section>

            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link 
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  Need help? <Link href="/contact" className="text-brand hover:text-[#2a6bb0] font-medium">Contact our team</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}